import { ApiError } from '../api/client'
import { plural } from '../lib/format'
import { AuthApiError } from './authApi'

export const NETWORK_ERROR_MESSAGE = 'Could not reach the server. Check your connection and try again.'

/** A wait in words, rounded up so it never promises less than the server asked for: "2 minutes". */
export function formatWait(seconds: number): string {
  const whole = Math.max(1, Math.ceil(seconds))
  if (whole < 60) return plural(whole, 'second')
  const minutes = Math.ceil(whole / 60)
  if (minutes < 60) return plural(minutes, 'minute')
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? plural(hours, 'hour') : `${plural(hours, 'hour')} ${plural(rest, 'minute')}`
}

/** A ticking countdown: "27s", "4:05", "1:02:03". */
export function formatCountdown(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds))
  if (whole < 60) return `${whole}s`
  const hours = Math.floor(whole / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const secs = String(whole % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`
}

function asSentence(text: string): string {
  const trimmed = text.trim()
  if (trimmed === '') return 'Something went wrong. Try again.'
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`
}

/**
 * What to tell people when a sign-in call fails: the server's own message (400, 503, ...), with the
 * wait added for 429, or a connection hint when the request never got an answer.
 */
export function describeAuthError(error: unknown): string {
  if (error instanceof AuthApiError && error.status === 429 && error.retryAfter !== null) {
    return `${asSentence(error.message)} Try again in ${formatWait(error.retryAfter)}.`
  }
  if (error instanceof ApiError) return asSentence(error.message)
  return NETWORK_ERROR_MESSAGE
}
